import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../providers/AuthContext";
import ConfirmationModal from "../components/ConfirmationModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import toast from "react-hot-toast";
import { FormCard } from "./FormCard";
import LoadingSpinner from "../components/LoadingSpinner";


const EntityMultiSelectForm = ({
    title,
    description,
    fetchItems,
    labelKey = "label",
    valueKey = "value",
    selectedIds = [],
    onChange,
    onItemsLoaded,
    reloadKey,
    confirmRemove = true,
}) => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const cardTitle = title || t("entityMulti.selectItems");

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [checked, setChecked] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const [checkAll, setCheckAll] = useState(false);

    useEffect(() => {
        setLoading(true);
        loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [reloadKey]);

    const loadItems = async () => {
        try {
            const token = await getAccessToken();
            const list = await fetchItems(token);

            setItems(list);
            onItemsLoaded?.(list);

            const initialChecked = {};
            list.forEach((i) => {
                const id = i[valueKey];
                initialChecked[id] = selectedIds.includes(id);
            });

            setChecked(initialChecked);
        } catch (err) {
            toast.error(err?.message || t("entityMulti.failedLoad"));
        }

        setLoading(false);
    };

    const unselected = items.filter((i) => !selectedIds.includes(i[valueKey]));

    const filtered = unselected.filter((i) =>
        i[labelKey]?.toLowerCase().includes(search.toLowerCase())
    );

    const handleAdd = () => {
        const selectedNew = Object.keys(checked)
            .filter((id) => checked[id])
            .filter((id) => !selectedIds.includes(id));

        onChange([...selectedIds, ...selectedNew]);

        const updated = { ...checked };
        selectedNew.forEach((id) => {
            updated[id] = false;
        });
        setChecked(updated);
    };

    const removeItem = (id) => {
        onChange(selectedIds.filter((sid) => sid !== id));
    };

    useEffect(() => {
        if (filtered.length === 0) {
            setCheckAll(false);
            return;
        }

        const allChecked = filtered.every((i) => checked[i[valueKey]]);
        setCheckAll(allChecked);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [checked, filtered]);

    if (loading) {
        return (
            <FormCard title={cardTitle} description={description}>
                <LoadingSpinner />
            </FormCard>
        );
    }


    return (
        <>
            <FormCard title={cardTitle} description={description}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="flex flex-col gap-3">
                        <input
                            type="text"
                            className="input-default"
                            placeholder={t("entityMulti.searchPlaceholder")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        <label className="flex items-center gap-2 text-sm font-medium">
                            {t("entityMulti.checkAll")}:
                            <input
                                type="checkbox"
                                checked={checkAll}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setCheckAll(isChecked);

                                    setChecked((prev) => {
                                        const updated = { ...prev };
                                        filtered.forEach((i) => {
                                            updated[i[valueKey]] = isChecked;
                                        });
                                        return updated;
                                    });
                                }}
                            />
                        </label>

                        <div className="border rounded-md p-3 max-h-56 overflow-y-auto bg-white">
                            {loading && (
                                <p className="text-sm text-mute-400">{t("common.loading")}</p>
                            )}

                            {!loading && filtered.length === 0 && (
                                <p className="text-sm text-mute-400">{t("entityMulti.noItemsFound")}</p>
                            )}

                            {!loading &&
                                filtered.map((i) => {
                                    const id = i[valueKey];
                                    return (
                                        <label
                                            key={id}
                                            className="flex items-center gap-2 py-1 cursor-pointer hover:bg-mute-50 px-1 rounded"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked[id] || false}
                                                onChange={(e) =>
                                                    setChecked((prev) => ({
                                                        ...prev,
                                                        [id]: e.target.checked,
                                                    }))
                                                }
                                            />
                                            <span>{i[labelKey]}</span>
                                        </label>
                                    );
                                })}
                        </div>

                        <button
                            className="btn-primary btn-sm mt-2 w-fit"
                            onClick={handleAdd}
                            disabled={filtered.length === 0}
                        >
                            {t("entityMulti.addSelected")}
                        </button>
                    </div>

                    <div>
                        <h4 className="text-sm font-semibold mb-2">{t("entityMulti.selected")}</h4>

                        <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-mute-50">
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-2">{t("common.name")}</th>
                                        <th className="text-left py-2 px-2">{t("common.remove")}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {selectedIds.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan="2"
                                                className="py-3 text-mute-400 text-center"
                                            >
                                                {t("entityMulti.noItemsSelected")}
                                            </td>
                                        </tr>
                                    )}

                                    {selectedIds.map((id) => {
                                        const item = items.find((i) => i[valueKey] === id);
                                        return (
                                            <tr key={id} className="border-b hover:bg-mute-50">
                                                <td className="py-2 px-2">{item?.[labelKey]}</td>

                                                <td className="py-2 px-2 text-center">
                                                    <button
                                                        onClick={() => {
                                                            if (confirmRemove) {
                                                                setItemToRemove(id);
                                                                setConfirmOpen(true);
                                                                return;
                                                            }
                                                            removeItem(id);
                                                        }}
                                                        className="text-error-500 hover:text-error-600 transition"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </FormCard>

            {confirmRemove && (
            <ConfirmationModal
                isOpen={confirmOpen}
                title={
                    itemToRemove
                        ? t("confirm.removeItem", {
                            name: items.find((i) => i[valueKey] === itemToRemove)?.[labelKey] || "",
                        })
                        : t("confirm.areYouSure")
                }
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    removeItem(itemToRemove);
                    setConfirmOpen(false);
                }}
            />
            )}
        </>
    );
};

export default EntityMultiSelectForm;
